import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Building2Icon } from 'lucide-react';
import { Button, Checkbox, Label, Skeleton } from '@tinct/web-components';
import {
  useGetAdsAccounts,
  useUpdateCrmIntegrationSettings,
} from '@tinct/core-api';

type AdAccountPickerProps = {
  integrationId: string;
  /** Offered after a save: the new selection only applies to the next sync. */
  onRequestSync?: () => void;
};

/**
 * Which LinkedIn ad accounts the sync covers. Every account checked (the
 * default, stored as an empty list) means "all accounts, including ones added
 * later"; a subset restricts the sync. Saving only changes the scope of the
 * next sync — hence the "Sync now" offer.
 */
export function AdAccountPicker({ integrationId, onRequestSync }: AdAccountPickerProps) {
  const { data: accounts, isLoading, isError, error } = useGetAdsAccounts(integrationId);

  // The server's selection (empty settings ⇒ every row selected) plus the
  // user's unsaved toggles on top — derived, so a refetch never clobbers an
  // in-progress edit and a save just drops the overrides.
  const [overrides, setOverrides] = useState<Map<string, boolean>>(new Map());
  const isChecked = (account: { customerId: string | null; selected: boolean | null }) =>
    overrides.get(account.customerId as string) ?? account.selected !== false;

  const allIds = useMemo(
    () => (accounts ?? []).map((a) => a.customerId).filter((id): id is string => !!id),
    [accounts],
  );
  // Cheap enough to derive on every render (a handful of accounts).
  const selection = new Set(
    (accounts ?? [])
      .filter((a) => a.customerId && isChecked(a))
      .map((a) => a.customerId as string),
  );
  const allChecked = allIds.length > 0 && allIds.every((id) => selection.has(id));
  const dirty = (accounts ?? []).some(
    (a) => a.customerId && isChecked(a) !== (a.selected !== false),
  );

  const updateSettings = useUpdateCrmIntegrationSettings({
    onSuccess: () => {
      setOverrides(new Map());
      toast.success('Account selection saved — it applies from the next sync', {
        action: onRequestSync
          ? { label: 'Sync now', onClick: onRequestSync }
          : undefined,
      });
    },
    onError: (err) => toast.error(err.error),
  });

  const toggle = (id: string, checked: boolean) => {
    setOverrides((prev) => new Map(prev).set(id, checked));
  };

  const handleSave = () => {
    updateSettings.mutate({
      integrationId,
      settings: {
        providerType: 'linkedin_ads',
        // Every account checked ⇒ empty list ⇒ "sync all" (future accounts too).
        adAccountIds: allChecked ? [] : allIds.filter((id) => selection.has(id)),
      },
    });
  };

  return (
    <section
      className="flex flex-col gap-4 rounded-xl border border-border bg-card px-5 py-4"
      data-testid="linkedin-ad-account-picker"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-col gap-1">
          <h3 className="text-sm font-medium text-foreground">Ad accounts</h3>
          <p className="max-w-xl text-sm text-muted-foreground">
            Choose which ad accounts to sync campaigns and metrics from. Leave
            every account checked to sync all of them, including accounts added
            later. Changes apply to the next sync.
          </p>
        </div>
        <Button
          size="sm"
          onClick={handleSave}
          disabled={!dirty || selection.size === 0 || updateSettings.isPending}
          isLoading={updateSettings.isPending}
        >
          Save
        </Button>
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-5 w-64" />
          <Skeleton className="h-5 w-52" />
        </div>
      ) : isError ? (
        <p className="text-sm text-destructive">
          {error?.error ?? 'Could not load the ad accounts from LinkedIn.'}
        </p>
      ) : allIds.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No ad account is readable with the connected LinkedIn member. Ask for
          at least a Viewer role on the ad account, then reconnect.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {(accounts ?? []).map((account) => {
            const id = account.customerId;
            if (!id) return null;
            const checkboxId = `ad-account-${id}`;
            return (
              <li key={id} className="flex items-center gap-3">
                <Checkbox
                  id={checkboxId}
                  checked={selection.has(id)}
                  onCheckedChange={(checked) => toggle(id, checked === true)}
                />
                <Label htmlFor={checkboxId} className="flex items-center gap-2 font-normal">
                  <Building2Icon className="size-4 text-muted-foreground" />
                  <span>{account.name ?? id}</span>
                  <span className="font-mono text-xs text-muted-foreground">{id}</span>
                  {account.currency && (
                    <span className="text-xs text-muted-foreground">{account.currency}</span>
                  )}
                  {account.manager && (
                    <span className="text-xs text-muted-foreground">· manager</span>
                  )}
                </Label>
              </li>
            );
          })}
        </ul>
      )}
      {selection.size === 0 && allIds.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Select at least one account.
        </p>
      )}
    </section>
  );
}
